import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
} from "lucide-react";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const parseIsoDate = (value) => {
  if (!value) return null;

  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
};

const formatIsoDate = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (date) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).formatToParts(date);

  const day = parts.find((part) => part.type === "day")?.value || "";
  const month = parts.find((part) => part.type === "month")?.value || "";
  const year = parts.find((part) => part.type === "year")?.value || "";

  return `${day} ${month}, ${year}`;
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const isSameDate = (left, right) =>
  Boolean(left) &&
  Boolean(right) &&
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const isWithinRange = (date, startDate, endDate) => {
  if (!startDate || !endDate) return false;

  const value = date.getTime();
  return value > startDate.getTime() && value < endDate.getTime();
};

const buildCalendarDays = (monthDate) => {
  const firstDayOfMonth = startOfMonth(monthDate);
  const calendarStart = new Date(firstDayOfMonth);
  calendarStart.setDate(calendarStart.getDate() - calendarStart.getDay());

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(calendarStart);
    date.setDate(calendarStart.getDate() + index);

    return {
      date,
      iso: formatIsoDate(date),
      isCurrentMonth: date.getMonth() === monthDate.getMonth(),
    };
  });
};

export default function DateRangePicker({
  startDate,
  endDate,
  onRangeChange,
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() =>
    startOfMonth(parseIsoDate(startDate) || new Date()),
  );
  // Set the instant a first click applies that single day immediately (see handleDateClick) -
  // kept separate from the startDate/endDate props so a second click, before the calendar
  // closes, can still stretch that day into a full range instead of starting over.
  const [pendingAnchor, setPendingAnchor] = useState(null);
  const pickerRef = useRef(null);

  const startDateValue = useMemo(() => parseIsoDate(startDate), [startDate]);
  const endDateValue = useMemo(() => parseIsoDate(endDate), [endDate]);
  const hasSelection = Boolean(startDateValue || endDateValue);
  const calendarDays = useMemo(
    () => buildCalendarDays(visibleMonth),
    [visibleMonth],
  );
  const rangeLabel = useMemo(() => {
    if (startDateValue && endDateValue) {
      return `Date: ${formatDisplayDate(startDateValue)} to ${formatDisplayDate(endDateValue)}`;
    }

    if (startDateValue) {
      return `Date: ${formatDisplayDate(startDateValue)}`;
    }

    return "Date: Select range";
  }, [endDateValue, startDateValue]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!pickerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    setVisibleMonth(startOfMonth(startDateValue || new Date()));
    // Deliberately not depending on startDateValue - a first click now applies its date
    // immediately (see handleDateClick), which changes startDateValue while the calendar is
    // still open for a possible second, range-extending click; re-running this on that change
    // would be harmless for the visible month but must not also be the trigger that clears
    // pendingAnchor, so that reset lives in its own effect keyed only on isOpen below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) setPendingAnchor(null);
  }, [isOpen]);

  const openCalendar = () => {
    if (disabled) return;
    setIsOpen(true);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openCalendar();
    }
  };

  const handleDateClick = (selectedDate) => {
    const selectedIsoDate = formatIsoDate(selectedDate);

    if (pendingAnchor) {
      // Second click - stretch the anchor day (applied on the first click below) into a
      // full range, ordering the two dates regardless of which was clicked first.
      const anchorIsoDate = formatIsoDate(pendingAnchor);
      if (selectedDate.getTime() >= pendingAnchor.getTime()) {
        onRangeChange(anchorIsoDate, selectedIsoDate);
      } else {
        onRangeChange(selectedIsoDate, anchorIsoDate);
      }
      setPendingAnchor(null);
      setIsOpen(false);
      return;
    }

    // First click applies immediately as a single-day filter instead of waiting for a
    // second click - the calendar stays open so that second click, if it comes, can still
    // extend this into a multi-day range via the branch above.
    onRangeChange(selectedIsoDate, selectedIsoDate);
    setPendingAnchor(selectedDate);
  };

  const clearSelection = () => {
    setIsOpen(false);
    setPendingAnchor(null);
    onRangeChange("", "");
  };

  return (
    <div
      ref={pickerRef}
      className={`home-date-range-picker${disabled ? " is-disabled" : ""}`}
    >
      <div className="home-date-range-picker__trigger-shell">
        <button
          type="button"
          className={`home-date-range-picker__trigger${
            hasSelection ? " has-inline-clear" : ""
          }`}
          onClick={openCalendar}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-label="Open date range calendar"
        >
          <span className="home-date-range-picker__trigger-icon">
            <Filter size={16} strokeWidth={2.1} />
          </span>
          <span className="home-date-range-picker__trigger-text">{rangeLabel}</span>
        </button>
        {hasSelection ? (
          <button
            type="button"
            className="home-date-range-picker__trigger-clear"
            onClick={(event) => {
              event.stopPropagation();
              clearSelection();
            }}
            aria-label="Clear selected date range"
          >
            <X size={14} strokeWidth={2.2} />
          </button>
        ) : null}
      </div>

      {isOpen && !disabled ? (
        <div className="home-date-range-picker__popover">
          <div className="home-date-range-picker__header">
            <button
              type="button"
              className="home-date-range-picker__nav"
              onClick={() =>
                setVisibleMonth(
                  new Date(
                    visibleMonth.getFullYear(),
                    visibleMonth.getMonth() - 1,
                    1,
                  ),
                )
              }
              aria-label="Previous month"
            >
              <ChevronLeft size={16} strokeWidth={2} />
            </button>
            <div className="home-date-range-picker__month">
              {visibleMonth.toLocaleString("en-US", {
                month: "long",
                year: "numeric",
              })}
            </div>
            <button
              type="button"
              className="home-date-range-picker__nav"
              onClick={() =>
                setVisibleMonth(
                  new Date(
                    visibleMonth.getFullYear(),
                    visibleMonth.getMonth() + 1,
                    1,
                  ),
                )
              }
              aria-label="Next month"
            >
              <ChevronRight size={16} strokeWidth={2} />
            </button>
          </div>

          <div className="home-date-range-picker__weekdays">
            {WEEKDAY_LABELS.map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>

          <div className="home-date-range-picker__grid">
            {calendarDays.map((day) => {
              const isSelectedStart = isSameDate(day.date, startDateValue);
              const isSelectedEnd = isSameDate(day.date, endDateValue);
              const isInRange = isWithinRange(
                day.date,
                startDateValue,
                endDateValue,
              );

              return (
                <button
                  key={day.iso}
                  type="button"
                  className={`home-date-range-picker__day${
                    day.isCurrentMonth ? "" : " is-outside-month"
                  }${isInRange ? " is-in-range" : ""}${
                    isSelectedStart ? " is-range-start" : ""
                  }${isSelectedEnd ? " is-range-end" : ""}`}
                  onClick={() => handleDateClick(day.date)}
                >
                  {day.date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="home-date-range-picker__footer">
            <div className="home-date-range-picker__summary">
              <span>{startDate || "Start not set"}</span>
              <span>{endDate || "End not set"}</span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
